import React, { useRef, useState, useEffect } from 'react';
import Events from '../../../../utilities/EventEmitter';
import { uniqBy } from 'lodash';
import { getPickupDataFromEvent } from '../utils';
import { RMAXLogger } from '../../../../services/LoggerService/RMAXLogger';
import appConstants from '../../../../appConstants';
import { logPickupEvent, getPickupProcessingCount, normalizePickupIds } from './utils';
import {
  handleTrailerMovePickupSuccess,
  shouldHandleAsTrailerMovePickupEvent,
} from './trailerMovePickupHandler';
import { handleTrailerMovesPickupEvent } from '../../../modules/Dispatch/RouteExecution/TrailerMoves/swapBoardService';
import pickupOperationsLogger from './PickupOperationsLogger';
import { alertNewToBeReviewedPickups } from '../Notifications/utils';
const LoaderDialog = React.lazy(() => import('../DispatchLoader'));

const PickupEventHandler = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [eventName, setEventName] = useState(null);
  const [processedPickupCount, setProcessedPickupCount] = useState(0);
  const pendingPickupEventRef = useRef(null);
  const failedPendingPickupsRef = useRef([]); // tracks failed pickup events
  const pendingUpdateDriverIdsRef = useRef([]);
  const nonPendingPickupsRef = useRef([]); //tracks pickup updates received while pending pickup event is in progress
  const processedPickupsRef = useRef([]);
  const timerRef = useRef(null);
  const pickupOperationTimeTrackingIdRef = useRef(null);

  useEffect(() => {
    Events.on('HandlePickupEvent', handlePickupEvent);
    Events.on('ShowLoaderDialog', showLoaderDialog);
    Events.on('HideLoaderDialog', triggerHideLoaderDialog);
    Events.on("PickupAssignmentErrorNotification",handlePickupAssignmentError)
    return () => {
      Events.off('HandlePickupEvent', handlePickupEvent);
      Events.off('ShowLoaderDialog', showLoaderDialog);
      Events.off('HideLoaderDialog', triggerHideLoaderDialog);
      Events.off("PickupAssignmentErrorNotification",handlePickupAssignmentError)
    };
  }, [showLoader, eventName]);

  useEffect(() => {
    clearTimeout(timerRef?.current);
  }, []);

  const showLoaderDialog = (event) => {
    if (
      [
        appConstants.PICKUP_EVENTS.ASSIGN_PICKUP,
        appConstants.PICKUP_EVENTS.UNASSIGN_PICKUP
      ].includes(event.eventName)
    ) {
      pickupOperationTimeTrackingIdRef.current = event.pickupOperationTimeTrackingId;
      pendingPickupEventRef.current = {
        eventType: event.eventName,
        pickupIds: event.pickupIds.map((id) => id.toUpperCase())
      };
    } else {
      pendingPickupEventRef.current = null;
    }
    updateProcessedPickupCount([]);
    setShowLoader(true);
    setEventName(event?.label || event);
    props.setDataRefreshProgress(true);

    timerRef.current = setTimeout(() => {
      //Start the timer
      RMAXLogger.warn(`Event TimeOut: ${eventName}`, {
        pendingPickupCount: pendingPickupEventRef.current?.pickupIds?.length ?? 0,
        pendingPickupIds: pendingPickupEventRef.current?.pickupIds,
        processedPickupCount: processedPickupsRef.current?.length ?? 0,
        failedPickupCount: failedPendingPickupsRef.current?.length ?? 0,
        nonPendingPickupCount: nonPendingPickupsRef.current?.length ?? 0
      });
      markPickupOperationAsTimedOut();
      failedPendingPickupsRef.current = [];
      hideLoaderDialog(); //After 1 min, hide loader
      props.refreshDispatchStore();
    }, 60000);
  };

  const hideLoaderDialog = () => {
    pendingPickupEventRef.current = null;
    if (eventName?.toLowerCase().includes('pickup')) {
      Events.emit('PickupAssignmentCompleted');
    }
    updateProcessedPickupCount([]);
    setShowLoader(false);
    setEventName('');
    props.setDataRefreshProgress(false);
    nonPendingPickupsRef.current = [];
    finalizeAndCleanupPickupTracking();
    clearTimeout(timerRef?.current);
  };

  const markPickupOperationAsTimedOut = () => {
    const pickupOperationTimeTrackingId = pickupOperationTimeTrackingIdRef.current;
    if (!pickupOperationTimeTrackingId) return;
    const pickupOperationTrackingData = pickupOperationsLogger.getOperationTrackingData(pickupOperationTimeTrackingId);
    if (pickupOperationTrackingData) {
      pickupOperationTrackingData.failureReason = "Operation timed out after 60s";
    }
  };

  const finalizeAndCleanupPickupTracking = () => {
    const pickupOperationTimeTrackingId = pickupOperationTimeTrackingIdRef.current;
    if (!pickupOperationTimeTrackingId) return;
    pickupOperationsLogger.finalizeOperationAndLogMetrics(pickupOperationTimeTrackingId);
    pickupOperationTimeTrackingIdRef.current = null;
  };

  const refreshDriversOnly = (driverIds) =>
    props.refreshDispatchStore({
      driverIds,
      refreshPickups: false,
      pickupIds: [],
      pickupOperationTimeTrackingId: pickupOperationTimeTrackingIdRef.current,
    });

  const refreshTrailerMoveSwapBoard = (pickupData, pickupEventName) => {
    if (!props.enableTrailerSwapBoard || !props.terminalCode) {
      return;
    }
    handleTrailerMovesPickupEvent({
      dispatchStore: props.DispatchStore,
      terminalCode: props.terminalCode,
      dataString: JSON.stringify(pickupData),
      eventName: pickupEventName,
    });
  };

  const notifyPickupStatusChangeForTracking = (pickupData) => {
    const pickupOperationTimeTrackingId = pickupOperationTimeTrackingIdRef.current;
    const pendingEvent = pendingPickupEventRef.current;
    
    // Only emit tracking event if we have both a tracking ID and a pending event with eventType
    if (!pickupOperationTimeTrackingId || !pendingEvent?.eventType) return;
    
    Events.emit(pendingEvent.eventType, pickupData.pickupId, pickupOperationTimeTrackingId);
  };

  const triggerHideLoaderDialog = ({ isAPICallFailed = false } = {}) => {
    if (isAPICallFailed) {
      hideLoaderDialog();
    }
    if (pendingPickupEventRef.current) {
      return;
    } else if (showLoader) {
      hideLoaderDialog();
    }
  };

  // Emit events for pickup optimizer log and assign/unassign pickup notifications
  const emitPickupEvents = (pickupData, eventName) => {
    if (props.isPickupOptimizationInProgress) {
      Events.emit('ShowpickupOptimizationLogs', pickupData, eventName);
    } else if (
      [
        appConstants.PICKUP_EVENTS.ASSIGN_PICKUP,
        appConstants.PICKUP_EVENTS.UNASSIGN_PICKUP
      ].includes(eventName)
    ) {
      const pendingPickupIds = pendingPickupEventRef.current?.pickupIds ?? [];
      Events.emit('showAssignPickupNotification', pickupData, eventName, pendingPickupIds);
    }
  };

  // Handle pickup events, logging, normalizing data, and emitting events based on pending status.
  const handlePickupEvent = (data, pickupEventName) => {
    let pickupData = JSON.parse(data);
    logPickupEvent(pickupData, pickupEventName);
    normalizePickupIds(pickupData);
    emitPickupEvents(pickupData, pickupEventName);

    if (pendingPickupEventRef.current) {
      notifyPickupStatusChangeForTracking(pickupData);
      handlePendingPickupEvent(pickupData, pickupEventName);
    } else {
      handleImmediatePickupEvent(pickupData, pickupEventName);
    }
  };

  // Handle pickup events with pending pickups: when pendingPickupEventRef is non empty, processing success or failure accordingly.
  const handlePendingPickupEvent = (pickupData, eventName) => {
    if (pickupData.success) {
      processSuccessPendingPickups(pickupData, eventName);
    } else {
      processFailurePendingPickups(pickupData);
    }
    finalizePendingPickups();
  };

  // Handle immediate pickup events: when pendingPickupEventRef is empty
  const handleImmediatePickupEvent = (pickupData, eventName) => {
    if (pickupData.success) {
      pendingUpdateDriverIdsRef.current = [
        ...pendingUpdateDriverIdsRef.current,
        pickupData.driverId
      ];
    } else {
      handleFailedPickupEvents(pickupData);
    }

    if (
      pickupData.success &&
      shouldHandleAsTrailerMovePickupEvent(pickupData, props.enableTrailerSwapBoard)
    ) {
      handleTrailerMovePickupSuccess(props.DispatchStore, pickupData, eventName);
      refreshTrailerMoveSwapBoard(pickupData, eventName);
      const driverIds = [...pendingUpdateDriverIdsRef.current];
      pendingUpdateDriverIdsRef.current = [];
      refreshDriversOnly(driverIds);
      return;
    }

    updateNonPendingPickups(pickupData, eventName);
  };

  // Update non-pending pickups based on success or failed events.
  const updateNonPendingPickups = (pickupData, eventName) => {
    const driverIds = [...pendingUpdateDriverIdsRef.current];
    pendingUpdateDriverIdsRef.current = [];

    // Handle multiple pickups from single event
    let updatedPickups = [];
    if (pickupData.success) {
      if (pickupData.pickupIds && pickupData.pickupIds.length > 1) {
        // Multiple pickups - create individual pickup data objects
        updatedPickups = pickupData.pickupIds.map((pickupId) => ({
          ...pickupData,
          pickupId: pickupId
        }));
      } else {
        // Single pickup
        updatedPickups = [pickupData];
      }
    }

    updatePickups({
      updatedPickups,
      eventName,
      driverIds,
      originalPickupData: pickupData
    });
  };

  // Process success pickup events with pending pickups list.
  const processSuccessPendingPickups = (pickupData, eventName) => {
    // Handle multiple pickups from single event - DEDUPLICATE pickup IDs
    const eventPickupIds = [...new Set(pickupData.pickupIds || [pickupData.pickupId])];
    const pendingPickupIds = pendingPickupEventRef.current.pickupIds;

    // Check if any of the event pickup IDs are in pending list
    const matchingPendingPickups = eventPickupIds.filter((pickupId) =>
      pendingPickupIds.includes(pickupId.toUpperCase())
    );

    if (
      matchingPendingPickups.length > 0 &&
      pendingPickupEventRef.current?.eventType?.toLowerCase() === eventName.toLowerCase()
    ) {
      // Remove matching pickups from pending list
      pendingPickupEventRef.current.pickupIds = pendingPickupEventRef.current.pickupIds.filter(
        (pendingPickupId) => !matchingPendingPickups.includes(pendingPickupId)
      );

      // Create pickup data objects for each processed pickup
      const processedPickupData = matchingPendingPickups.map((pickupId) => ({
        ...pickupData,
        pickupId: pickupId
      }));

      updateProcessedPickupCount([...processedPickupsRef.current, ...processedPickupData]);

      //Remove pickups from nonPendingPickupsRef list once they are added to processedPickupsRef list.
      nonPendingPickupsRef.current = nonPendingPickupsRef.current.filter(
        (pickup) => !matchingPendingPickups.includes(pickup.pickupId)
      );
    } else {
      // If no matching pending pickups, add to non-pending list
      nonPendingPickupsRef.current = [...nonPendingPickupsRef.current, pickupData];
    }

    pendingUpdateDriverIdsRef.current = [...pendingUpdateDriverIdsRef.current, pickupData.driverId];

    // Remove processed pickup IDs from failed list
    failedPendingPickupsRef.current = failedPendingPickupsRef.current.filter(
      (failedPickupId) => !eventPickupIds.includes(failedPickupId)
    );
  };

  const updateProcessedPickupCount = (processedPickups) => {
    // Deduplicate processed pickups by pickupId to prevent duplicate counting
    const uniqueProcessedPickups = processedPickups.filter((pickup, index, self) => 
      index === self.findIndex(p => p.pickupId === pickup.pickupId)
    );
    
    processedPickupsRef.current = uniqueProcessedPickups;
    setProcessedPickupCount(getPickupProcessingCount(processedPickupsRef, pendingPickupEventRef));
  };

  /*
    Logic to process failure pending pickups, 
    Collect driver/pickup IDs for failed events and update pending pickup list.
  */
  const processFailurePendingPickups = (pickupData) => {
    handleFailedPickupEvents(pickupData);
    pendingPickupEventRef.current.pickupIds = pendingPickupEventRef.current.pickupIds.filter(
      (pendingPickupId) => !pickupData.pickupIds.includes(pendingPickupId.toUpperCase())
    );
  };

  // Collect driver ID and pickup IDs for failed pickup events.
  const handleFailedPickupEvents = ({ pickupIds, driverId }) => {
    failedPendingPickupsRef.current = [...failedPendingPickupsRef.current, ...pickupIds];
    pendingUpdateDriverIdsRef.current = [...pendingUpdateDriverIdsRef.current, driverId];
  };

  const finalizePendingPickups = () => {
    if (!pendingPickupEventRef.current?.pickupIds?.length) {
      const eventName = pendingPickupEventRef.current?.eventType;
      const driverIds = [...pendingUpdateDriverIdsRef.current];
      pendingUpdateDriverIdsRef.current = [];
      pendingPickupEventRef.current = null;
      const updatedPickups = uniqBy(
        //contains success event data
        [...processedPickupsRef.current, ...nonPendingPickupsRef.current].reverse(),
        'pickupId'
      ); // Contains latest and unique pickup SignalR data.

      updatedPickups
        .filter((pickup) =>
          pickup.success &&
          shouldHandleAsTrailerMovePickupEvent(pickup, props.enableTrailerSwapBoard)
        )
        .forEach((pickup) => {
          handleTrailerMovePickupSuccess(props.DispatchStore, pickup, eventName);
          refreshTrailerMoveSwapBoard(pickup, eventName);
        });

      const regularPickups = updatedPickups.filter(
        (pickup) => !shouldHandleAsTrailerMovePickupEvent(pickup, props.enableTrailerSwapBoard)
      );

      // Update pickups for success/failed events.
      if (regularPickups.length || failedPendingPickupsRef.current.length) {
        updatePickups({
          updatedPickups: regularPickups,
          eventName,
          driverIds,
          originalPickupData: null
        });
      } else if (
        updatedPickups.some(
          (pickup) =>
            pickup.success &&
            shouldHandleAsTrailerMovePickupEvent(pickup, props.enableTrailerSwapBoard)
        )
      ) {
        if (props.enableHidePickupLoaderOnSignalR) {
          hideLoaderDialog();
        }
        refreshDriversOnly(driverIds);
      } else {
        triggerHideLoaderDialog();
      }
    }
  };

  // Update pickup data based on success or failed events, optionally refreshing pickups if necessary.
  const updatePickups = ({ updatedPickups, eventName, driverIds, originalPickupData }) => {
    let refreshPickups = false;
    let pickupIds = [];
    let allUpdatedPickupsPresentInStore = true;

    // Process success events.
    if (updatedPickups.length) {
      // Check if this is a multiple pickup event (from merged pickups, etc.)
      const isMultiplePickupEvent = updatedPickups.some(pickup => 
        pickup.pickupIds && pickup.pickupIds.length > 1
      );
      
      if (isMultiplePickupEvent) {
        // Multiple pickups - use the original pickupIds array from input data
        if (originalPickupData && originalPickupData.pickupIds) {
          pickupIds = originalPickupData.pickupIds.map((pickupId) => pickupId?.toUpperCase());
        } else {
          // Fallback: extract pickupIds from the first pickup object
          pickupIds = updatedPickups.map((pickup) => pickup.pickupId?.toUpperCase());
        }
        refreshPickups = true;
      } else {
        // Single pickup - use individual pickupId fields
        pickupIds = updatedPickups.map((pickup) => pickup.pickupId?.toUpperCase());

        // Single pickup - use original store update logic
        const existingPickupsInStore = props.DispatchStore.pickupsList.map((pickup) =>
          pickup.pickupId?.toUpperCase()
        );
        allUpdatedPickupsPresentInStore = updatedPickups.every((updatedPickup) =>
          existingPickupsInStore.includes(updatedPickup.pickupId?.toUpperCase())
        );
    
        if (allUpdatedPickupsPresentInStore) {
          props.DispatchStore.updateMultiplePickups(
            getPickupDataFromEvent(updatedPickups, eventName)
          );
          alertNewToBeReviewedPickups(
            props.DispatchStore,
            props.isNotificationPanelOpen
          );
        } else {
          refreshPickups = true;
        }
      }
    }

    // Process failed events.
    if (failedPendingPickupsRef.current.length) {
      refreshPickups = true;
      pickupIds = [
        ...(allUpdatedPickupsPresentInStore ? [] : pickupIds), // Include success pickupIds if one of the pickups is not available in store.
        ...failedPendingPickupsRef.current
      ];
      failedPendingPickupsRef.current = [];
    }

    if(props.enableHidePickupLoaderOnSignalR) {
      hideLoaderDialog();
    }

    props.refreshDispatchStore({ 
      driverIds, 
      refreshPickups, 
      pickupIds, 
      pickupOperationTimeTrackingId: pickupOperationTimeTrackingIdRef.current 
    });
  };

  const handlePickupAssignmentError = (apiErrorMsg) => {
    props.enqueueSnackbar(apiErrorMsg, { variant: 'error' });
  };

  return showLoader ? (
    <LoaderDialog eventName={`${eventName} ${processedPickupCount}`.trim()} />
  ) : null;
};

export default PickupEventHandler;
